import { EventType } from "@/analytics/analytics-events";
import { changeMode } from "@/store/mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import { useAppDispatch } from "@/store/store-hooks";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { FaroText } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { type GUID } from "@faro-lotv/foundation";
import {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  type PropsWithChildren,
} from "react";
import { useLoadAreaIfNecessary } from "./use-load-area-if-necessary";

export type ElementLinkProps = {
  /** The ID of the scene element to navigate to */
  elementId: GUID;

  /** Optional method that will be called after the mode change */
  onClick?(): void;
};

/**
 * @returns a clickable link that navigates to a scene element by ID.
 * If the element's area is not yet loaded when the user clicks, the navigation is deferred
 * until loading completes, showing a spinner in the meantime.
 */
export function ElementLink({
  children,
  elementId,
  onClick,
}: PropsWithChildren<ElementLinkProps>): ReactNode {
  const dispatch = useAppDispatch();

  const { navigable, loading } = useLoadAreaIfNecessary(elementId);

  // Tracks whether the user clicked while the area was still loading,
  // so navigation can be resumed once loading completes.
  const pendingNavigation = useRef(false);

  // Dispatches the store actions to select the element, switch to walk mode, and invoke the optional callback.
  const switchToElement = useCallback(() => {
    Analytics.track(EventType.clickOnAiChatLink);

    dispatch(setActiveElement(elementId));
    dispatch(changeMode("walk"));

    onClick?.();
  }, [dispatch, elementId, onClick]);

  // Navigate to the scene if the navigation is requested but only once the corresponding area is loaded.
  useEffect(() => {
    if (!loading && pendingNavigation.current) {
      pendingNavigation.current = false;
      dispatch(setShowSpinner(false));

      switchToElement();
    }
  }, [loading, dispatch, elementId, onClick, switchToElement]);

  // If the area is still loading, defers navigation and shows a spinner; otherwise navigates immediately.
  const loadAndSwitchToElement = useCallback(() => {
    if (loading) {
      pendingNavigation.current = true;
      dispatch(setShowSpinner(true));
      return;
    }

    switchToElement();
  }, [dispatch, loading, switchToElement]);

  if (!navigable) return children;

  return (
    <FaroText
      variant="hyperLink"
      onClick={loadAndSwitchToElement}
      sx={{
        cursor: "pointer",

        // Adjusting font to fit into rest of markdown
        fontSize: "1em",
      }}
    >
      {children}
    </FaroText>
  );
}
